import { useState, useEffect, useRef, useCallback } from 'react';

interface ViewerMemorySettingsProps {
  serverUrl: string;
}

interface ViewerMemoryConfig {
  enabled: boolean;
}

interface StreamerMemoryConfig {
  enabled: boolean;
}

interface AdaptiveResponseConfig {
  enabled: boolean;
  profile: 'conservative' | 'balanced' | 'aggressive';
  learningRate: number;
  explorationRate: number;
}

interface AIGuardrailConfig {
  strictMode: boolean;
  allowIdleVisionMemoryWrites: boolean;
  allowIdleVisionViewerMemoryWrites: boolean;
  maxToolCallsPerTurn: number;
}

interface StreamerMemoryEntry {
  id: string;
  factText: string;
  category: string;
  salienceScore: string;
  status: 'active' | 'archived' | 'suppressed';
  updatedAt: string;
}

type MemoryTab = 'personal' | 'community' | 'audience';

interface CommunityLore {
  history: Array<{
    id: string;
    eventType: string;
    description: string;
    importance: number;
    createdAt: string;
  }>;
  viewerLore: Array<{
    id: string;
    content: string;
    loreType: string;
    createdAt: string;
    viewerName: string;
  }>;
}

// Helper to get CSRF token from cookie
function getCsrfToken(): string | null {
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? match[1] : null;
}

// Streamer memory section definitions
const STREAMER_MEMORY_SECTIONS = [
  { key: 'STREAMER VIBE', label: 'Streamer Vibe', icon: '✨', color: 'purple' },
  { key: 'STREAM INFO', label: 'Stream Info', icon: '📺', color: 'blue' },
  { key: 'PERSONAL LIFE', label: 'Personal Life', icon: '🏠', color: 'green' },
  { key: 'INTERESTS & TOPICS', label: 'Interests & Topics', icon: '💬', color: 'orange' },
  { key: 'SHARED HISTORY', label: 'Shared History', icon: '🤝', color: 'pink' },
] as const;

// Parse structured streamer notes into sections
function parseStreamerNotes(notes: string): Record<string, string> {
  const sections: Record<string, string> = {};

  for (const section of STREAMER_MEMORY_SECTIONS) {
    const regex = new RegExp(`${section.key}:\\s*(.+?)(?=(?:${STREAMER_MEMORY_SECTIONS.map(s => s.key).join('|')}:|$))`, 'is');
    const match = notes.match(regex);
    if (match && match[1]) {
      const value = match[1].trim();
      if (value && value.toLowerCase() !== 'none yet') {
        sections[section.key] = value;
      }
    }
  }

  return sections;
}

// Check if notes are in the new structured format
function isStructuredFormat(notes: string): boolean {
  return STREAMER_MEMORY_SECTIONS.some(s => notes.includes(`${s.key}:`));
}

export function ViewerMemorySettings({ serverUrl }: ViewerMemorySettingsProps) {
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [streamerNotes, setStreamerNotes] = useState<string | null>(null);
  const [clearingStreamer, setClearingStreamer] = useState(false);
  const [notesRevealed, setNotesRevealed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const [config, setConfig] = useState<ViewerMemoryConfig>({
    enabled: false,
  });

  const [streamerConfig, setStreamerConfig] = useState<StreamerMemoryConfig>({
    enabled: true,
  });
  const [savingStreamerConfig, setSavingStreamerConfig] = useState(false);
  const [adaptiveConfig, setAdaptiveConfig] = useState<AdaptiveResponseConfig>({
    enabled: true,
    profile: 'balanced',
    learningRate: 0.12,
    explorationRate: 0.08,
  });
  const [guardrailConfig, setGuardrailConfig] = useState<AIGuardrailConfig>({
    strictMode: true,
    allowIdleVisionMemoryWrites: true,
    allowIdleVisionViewerMemoryWrites: false,
    maxToolCallsPerTurn: 3,
  });
  const [memoryEntries, setMemoryEntries] = useState<StreamerMemoryEntry[]>([]);
  const [loadingMemoryEntries, setLoadingMemoryEntries] = useState(false);
  const [savingAdaptiveConfig, setSavingAdaptiveConfig] = useState(false);
  const [savingGuardrails, setSavingGuardrails] = useState(false);
  const [runningDecay, setRunningDecay] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);

  const [communityLore, setCommunityLore] = useState<CommunityLore | null>(null);
  const [loadingLore, setLoadingLore] = useState(false);
  const [clearingLore, setClearingLore] = useState(false);
  const [showLoreClearConfirm, setShowLoreClearConfirm] = useState(false);
  const [loreRevealed, setLoreRevealed] = useState(false);

  const [activeTab, setActiveTab] = useState<MemoryTab>('personal');

  const [isEditingLore, setIsEditingLore] = useState(false);
  const [editLoreText, setEditLoreText] = useState('');
  const [savingLore, setSavingLore] = useState(false);

  // Track if initial load is complete to avoid saving on mount
  const initialLoadComplete = useRef(false);

  useEffect(() => {
    fetch(`${serverUrl}/api/settings/viewer-memory`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data: { error?: string; viewerMemory?: { enabled: boolean } }) => {
        if (data && !data.error && data.viewerMemory) {
          setConfig({
            enabled: data.viewerMemory.enabled ?? false,
          });
        }
        initialLoadComplete.current = true;
      })
      .catch(() => {
        initialLoadComplete.current = true;
      });

    fetch(`${serverUrl}/api/settings/streamer-notes`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data: { error?: string; streamerNotes?: string | null }) => {
        if (data && !data.error) {
          setStreamerNotes(data.streamerNotes ?? null);
        }
      })
      .catch(() => { });

    fetch(`${serverUrl}/api/settings/streamer-memory`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data: { error?: string; streamerMemory?: { enabled: boolean } }) => {
        if (data && !data.error && data.streamerMemory) {
          setStreamerConfig({
            enabled: data.streamerMemory.enabled ?? true,
          });
        }
      })
      .catch(() => { });

    fetch(`${serverUrl}/api/settings/adaptive-response`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data: { error?: string; adaptiveResponse?: { enabled: boolean; profile: string; learningRate: number; explorationRate: number } }) => {
        if (data && !data.error && data.adaptiveResponse) {
          setAdaptiveConfig({
            enabled: data.adaptiveResponse.enabled ?? true,
            profile: data.adaptiveResponse.profile === 'conservative' || data.adaptiveResponse.profile === 'balanced' || data.adaptiveResponse.profile === 'aggressive' ? data.adaptiveResponse.profile : 'balanced',
            learningRate: data.adaptiveResponse.learningRate ?? 0.12,
            explorationRate: data.adaptiveResponse.explorationRate ?? 0.08,
          });
        }
      })
      .catch(() => { });

    fetch(`${serverUrl}/api/settings/ai-guardrails`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data: { error?: string; aiGuardrails?: Record<string, unknown> }) => {
        if (data && !data.error && data.aiGuardrails) {
          setGuardrailConfig({
            strictMode: (data.aiGuardrails.strictMode as boolean) ?? true,
            allowIdleVisionMemoryWrites:
              (data.aiGuardrails.allowIdleVisionMemoryWrites as boolean)
              ?? (data.aiGuardrails.allowMemoryWritesInPassivePtt as boolean)
              ?? true,
            allowIdleVisionViewerMemoryWrites:
              (data.aiGuardrails.allowIdleVisionViewerMemoryWrites as boolean)
              ?? (data.aiGuardrails.allowViewerMemoryWritesInPassivePtt as boolean)
              ?? false,
            maxToolCallsPerTurn: (data.aiGuardrails.maxToolCallsPerTurn as number) ?? 3,
          });
        }
      })
      .catch(() => { });

    setLoadingMemoryEntries(true);
    fetch(`${serverUrl}/api/settings/streamer-memory/entries?status=active&limit=20`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data: { error?: string; entries: StreamerMemoryEntry[] }) => {
        if (data && !data.error && Array.isArray(data.entries)) {
          setMemoryEntries(data.entries);
        }
      })
      .catch(() => { })
      .finally(() => setLoadingMemoryEntries(false));

    setLoadingLore(true);
    fetch(`${serverUrl}/api/settings/community-lore`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data: { error?: string; lore: CommunityLore }) => {
        if (data && !data.error) {
          setCommunityLore(data.lore);
        }
      })
      .catch(() => { })
      .finally(() => setLoadingLore(false));
  }, [serverUrl]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!profileMenuRef.current) return;
      if (!profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const saveSettings = useCallback(async (settings: ViewerMemoryConfig) => {
    setSaveStatus('saving');
    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (csrfToken) {
        headers['x-csrf-token'] = csrfToken;
      }

      const res = await fetch(`${serverUrl}/api/settings/viewer-memory`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(settings),
      });

      if (res.ok) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } else {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [serverUrl]);

  const handleToggle = useCallback(() => {
    if (!initialLoadComplete.current) return;
    const newConfig = { ...config, enabled: !config.enabled };
    setConfig(newConfig);
    void saveSettings(newConfig);
  }, [config, saveSettings]);

  // Save streamer memory config
  const saveStreamerConfig = useCallback(async (newConfig: StreamerMemoryConfig) => {
    setSavingStreamerConfig(true);
    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (csrfToken) {
        headers['x-csrf-token'] = csrfToken;
      }

      await fetch(`${serverUrl}/api/settings/streamer-memory`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(newConfig),
      });
    } catch {
      // Ignore errors
    } finally {
      setSavingStreamerConfig(false);
    }
  }, [serverUrl]);

  const handleStreamerMemoryToggle = useCallback(() => {
    const newConfig = { ...streamerConfig, enabled: !streamerConfig.enabled };
    setStreamerConfig(newConfig);
    void saveStreamerConfig(newConfig);
  }, [streamerConfig, saveStreamerConfig]);

  const saveAdaptiveConfig = useCallback(async (newConfig: AdaptiveResponseConfig) => {
    setSavingAdaptiveConfig(true);
    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (csrfToken) headers['x-csrf-token'] = csrfToken;

      await fetch(`${serverUrl}/api/settings/adaptive-response`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(newConfig),
      });
    } catch {
      // Ignore
    } finally {
      setSavingAdaptiveConfig(false);
    }
  }, [serverUrl]);

  const saveGuardrailConfig = useCallback(async (newConfig: AIGuardrailConfig) => {
    setSavingGuardrails(true);
    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (csrfToken) headers['x-csrf-token'] = csrfToken;

      await fetch(`${serverUrl}/api/settings/ai-guardrails`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(newConfig),
      });
    } catch {
      // Ignore
    } finally {
      setSavingGuardrails(false);
    }
  }, [serverUrl]);

  const runMemoryDecay = useCallback(async () => {
    setRunningDecay(true);
    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = {};
      if (csrfToken) headers['x-csrf-token'] = csrfToken;

      await fetch(`${serverUrl}/api/settings/streamer-memory/decay`, {
        method: 'POST',
        headers,
        credentials: 'include',
      });

      const refreshed = await fetch(`${serverUrl}/api/settings/streamer-memory/entries?status=active&limit=20`, {
        credentials: 'include',
      });
      const data: { error?: string; entries: StreamerMemoryEntry[] } = await refreshed.json() as { error?: string; entries: StreamerMemoryEntry[] };
      if (data && !data.error && Array.isArray(data.entries)) {
        setMemoryEntries(data.entries);
      }
    } catch {
      // Ignore
    } finally {
      setRunningDecay(false);
    }
  }, [serverUrl]);

  const handleClearStreamerMemory = useCallback(async () => {
    setShowClearConfirm(false);
    setClearingStreamer(true);
    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = {};
      if (csrfToken) {
        headers['x-csrf-token'] = csrfToken;
      }

      const res = await fetch(`${serverUrl}/api/settings/streamer-notes`, {
        method: 'DELETE',
        headers,
        credentials: 'include',
      });

      if (res.ok) {
        setStreamerNotes(null);
        setMemoryEntries([]);
        setNotesRevealed(false);
        setIsEditing(false);
      }
    } catch {
      // Ignore errors
    } finally {
      setClearingStreamer(false);
    }
  }, [serverUrl]);

  const handleEditStreamerMemory = useCallback(() => {
    setEditText(streamerNotes || '');
    setIsEditing(true);
    setNotesRevealed(true);
  }, [streamerNotes]);

  const handleSaveStreamerMemory = useCallback(async () => {
    setSavingNotes(true);
    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (csrfToken) {
        headers['x-csrf-token'] = csrfToken;
      }

      const res = await fetch(`${serverUrl}/api/settings/streamer-notes`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ streamerNotes: editText.trim() || null }),
      });

      if (res.ok) {
        setStreamerNotes(editText.trim() || null);
        setIsEditing(false);
      }
    } catch {
      // Ignore errors
    } finally {
      setSavingNotes(false);
    }
  }, [serverUrl, editText]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditText('');
  }, []);

  const handleClearCommunityLore = useCallback(async () => {
    setShowLoreClearConfirm(false);
    setClearingLore(true);
    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = {};
      if (csrfToken) {
        headers['x-csrf-token'] = csrfToken;
      }

      const res = await fetch(`${serverUrl}/api/settings/community-lore`, {
        method: 'DELETE',
        headers,
        credentials: 'include',
      });

      if (res.ok) {
        setCommunityLore({ history: [], viewerLore: [] });
      }
    } catch {
      // Ignore errors
    } finally {
      setClearingLore(false);
    }
  }, [serverUrl]);

  const handleEditLore = useCallback(() => {
    if (!communityLore) return;
    const text = communityLore.history.map(h => h.description).join('\n');
    setEditLoreText(text);
    setIsEditingLore(true);
    setLoreRevealed(true);
  }, [communityLore]);

  const handleSaveLore = useCallback(async () => {
    setSavingLore(true);
    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (csrfToken) {
        headers['x-csrf-token'] = csrfToken;
      }

      const items = editLoreText
        .split('\n')
        .map(s => s.trim())
        .filter(s => s.length > 0)
        .map(description => ({ description, importance: 5 }));

      const res = await fetch(`${serverUrl}/api/settings/community-lore`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ items }),
      });

      if (res.ok) {
        // Refresh lore after save
        const loreRes = await fetch(`${serverUrl}/api/settings/community-lore`, { credentials: 'include' });
        const loreData: { error?: string; lore: CommunityLore } = await loreRes.json() as { error?: string; lore: CommunityLore };
        if (loreData && !loreData.error) {
          setCommunityLore(loreData.lore);
        }
        setIsEditingLore(false);
      }
    } catch {
      // Ignore errors
    } finally {
      setSavingLore(false);
    }
  }, [serverUrl, editLoreText]);

  const hasLore = communityLore && (communityLore.history.length > 0 || communityLore.viewerLore.length > 0);

  return (
    <div className="space-y-4">
      {/* Save Status */}
      {saveStatus !== 'idle' && (
        <div className="flex items-center justify-end">
          <div className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full transition-all duration-500 ${saveStatus === 'saving' ? 'bg-blue-500/10 text-blue-400 animate-pulse' :
            saveStatus === 'saved' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
            }`}>
            {saveStatus === 'saving' ? 'Syncing...' :
              saveStatus === 'saved' ? 'Synced to Cloud' : 'Sync Error'}
          </div>
        </div>
      )}

      {/* Tab Switcher */}
      <div className="grid gap-2 rounded-[24px] border border-cyan-300/[0.12] bg-[#050918]/[0.72] p-2 shadow-[0_18px_56px_rgba(0,0,0,0.28)] sm:grid-cols-3">
        {[
          { id: 'personal', label: 'About You', desc: 'Your style and preferences' },
          { id: 'community', label: 'Stream Moments', desc: 'Memes, wins, and channel history' },
          { id: 'audience', label: 'Viewers', desc: 'Returning viewer memory' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as MemoryTab)}
            className={`rounded-[18px] border px-4 py-3 text-left transition-all duration-300 ${activeTab === tab.id
              ? 'border-fuchsia-300/45 bg-[linear-gradient(110deg,rgba(37,99,235,0.34),rgba(92,45,202,0.28)_48%,rgba(190,24,171,0.30))] text-white shadow-[0_0_28px_rgba(59,130,246,0.16)]'
              : 'border-transparent text-slate-400 hover:border-cyan-300/[0.14] hover:bg-cyan-300/[0.055] hover:text-slate-100'
              }`}
          >
            <span className="block text-[13px] font-black text-white">{tab.label}</span>
            <span className="mt-1 block text-[11px] font-medium text-slate-300/65">{tab.desc}</span>
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="min-h-[400px] animate-in fade-in duration-500">
        {activeTab === 'personal' && (
          <div className="space-y-4 animate-in slide-in-from-left-2 duration-300">
            <div className="relative overflow-hidden rounded-[24px] border border-fuchsia-300/[0.14] bg-[linear-gradient(135deg,rgba(15,23,42,0.78),rgba(15,13,35,0.86))] p-4 shadow-[0_18px_52px_rgba(0,0,0,0.20)]">
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <span className="text-4xl">👤</span>
              </div>
              <h3 className="text-xl font-black text-white">About you</h3>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300/72">
                Your vibe, favorite games, inside jokes, and the details StreamBuddy should carry into future streams.
              </p>
            </div>

            <div className="space-y-4">
              {/* Toggle Control */}
              <div className="flex items-center justify-between gap-4 rounded-[22px] border border-white/10 bg-black/20 p-4">
                <div>
                  <span className="text-sm font-black text-white">Remember things about me</span>
                  <p className="mt-1 text-xs text-slate-400">StreamBuddy can remember your style and preferences between streams.</p>
                </div>
                <button
                  onClick={handleStreamerMemoryToggle}
                  disabled={savingStreamerConfig}
                  className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${streamerConfig.enabled ? 'bg-cyan-500' : 'bg-slate-700'
                    } ${savingStreamerConfig ? 'opacity-50' : ''}`}
                >
                  <div className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-transform ${streamerConfig.enabled ? 'left-6' : 'left-1'
                    }`} />
                </button>
              </div>

              {/* Adaptive response + guardrails */}
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <div className="space-y-3 rounded-[22px] border border-cyan-300/[0.12] bg-cyan-400/[0.045] p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-black text-white">Reply feel</h4>
                      <p className="mt-1 text-xs text-slate-400">Let StreamBuddy adjust to your stream over time.</p>
                    </div>
                    <button
                      onClick={() => {
                        const next = { ...adaptiveConfig, enabled: !adaptiveConfig.enabled };
                        setAdaptiveConfig(next);
                        void saveAdaptiveConfig(next);
                      }}
                      disabled={savingAdaptiveConfig}
                      className={`relative h-7 w-12 rounded-full transition-colors ${adaptiveConfig.enabled ? 'bg-cyan-500' : 'bg-slate-700'} ${savingAdaptiveConfig ? 'opacity-50' : ''}`}
                    >
                      <div className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-transform ${adaptiveConfig.enabled ? 'left-6' : 'left-1'}`} />
                    </button>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-cyan-200/80">Style</label>
                    <div ref={profileMenuRef} className="relative">
                      <button
                        type="button"
                        onClick={() => setProfileMenuOpen((v) => !v)}
                        disabled={savingAdaptiveConfig}
                        className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-left text-xs text-cyan-100 focus:border-cyan-400/50 focus:outline-none disabled:opacity-50"
                      >
                        <span>{adaptiveConfig.profile === 'conservative' ? 'Careful' : adaptiveConfig.profile === 'aggressive' ? 'Bold' : 'Balanced'}</span>
                        <span className="text-cyan-300">{profileMenuOpen ? '▴' : '▾'}</span>
                      </button>

                      {profileMenuOpen && (
                        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-cyan-400/25 bg-[#07162a] shadow-2xl">
                          {([
                            ['conservative', 'Careful'],
                            ['balanced', 'Balanced'],
                            ['aggressive', 'Bold'],
                          ] as const).map(([profile, label]) => (
                            <button
                              key={profile}
                              type="button"
                              onClick={() => {
                                const next = { ...adaptiveConfig, profile };
                                setAdaptiveConfig(next);
                                setProfileMenuOpen(false);
                                void saveAdaptiveConfig(next);
                              }}
                              className={`w-full px-3 py-2 text-left text-xs transition-colors ${adaptiveConfig.profile === profile
                                ? 'bg-cyan-500/20 text-cyan-100'
                                : 'text-cyan-200 hover:bg-cyan-500/10'
                                }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-[11px] leading-5 text-cyan-100/62">Balanced is the normal choice. Careful keeps replies safer. Bold lets StreamBuddy take bigger swings.</p>
                  </div>
                </div>

                <div className="space-y-3 rounded-[22px] border border-emerald-300/[0.12] bg-emerald-400/[0.04] p-4">
                  <div>
                    <h4 className="text-sm font-black text-white">Safety limits</h4>
                    <p className="mt-1 text-xs text-slate-400">Keep memory useful without letting it get noisy.</p>
                  </div>

                  <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-3 py-3">
                    <span className="text-xs font-black text-emerald-50">Ask before making bigger changes</span>
                    <button
                      onClick={() => {
                        const next = { ...guardrailConfig, strictMode: !guardrailConfig.strictMode };
                        setGuardrailConfig(next);
                        void saveGuardrailConfig(next);
                      }}
                      disabled={savingGuardrails}
                      className={`relative w-11 h-6 rounded-full transition-colors ${guardrailConfig.strictMode ? 'bg-emerald-500' : 'bg-gray-700'} ${savingGuardrails ? 'opacity-50' : ''}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${guardrailConfig.strictMode ? 'left-6' : 'left-1'}`} />
                    </button>
                  </div>
                  <p className="-mt-1 px-1 text-[11px] text-emerald-100/55">StreamBuddy stays more cautious before saving, changing, or using important memory.</p>

                  <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-3 py-3">
                    <span className="text-xs font-black text-emerald-50">Save useful moments from the screen</span>
                    <button
                      onClick={() => {
                        const next = { ...guardrailConfig, allowIdleVisionMemoryWrites: !guardrailConfig.allowIdleVisionMemoryWrites };
                        setGuardrailConfig(next);
                        void saveGuardrailConfig(next);
                      }}
                      disabled={savingGuardrails}
                      className={`relative w-11 h-6 rounded-full transition-colors ${guardrailConfig.allowIdleVisionMemoryWrites ? 'bg-emerald-500' : 'bg-gray-700'} ${savingGuardrails ? 'opacity-50' : ''}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${guardrailConfig.allowIdleVisionMemoryWrites ? 'left-6' : 'left-1'}`} />
                    </button>
                  </div>
                  <p className="-mt-1 px-1 text-[11px] text-emerald-100/55">When vision is on, StreamBuddy can remember things like games, scenes, wins, or repeated stream jokes.</p>

                  <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-3 py-3">
                    <span className="text-xs font-black text-emerald-50">Remember which viewer was involved</span>
                    <button
                      onClick={() => {
                        const next = {
                          ...guardrailConfig,
                          allowIdleVisionViewerMemoryWrites: !guardrailConfig.allowIdleVisionViewerMemoryWrites,
                        };
                        setGuardrailConfig(next);
                        void saveGuardrailConfig(next);
                      }}
                      disabled={savingGuardrails || !guardrailConfig.allowIdleVisionMemoryWrites}
                      className={`relative w-11 h-6 rounded-full transition-colors ${guardrailConfig.allowIdleVisionViewerMemoryWrites ? 'bg-emerald-500' : 'bg-gray-700'} ${savingGuardrails || !guardrailConfig.allowIdleVisionMemoryWrites ? 'opacity-50' : ''}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${guardrailConfig.allowIdleVisionViewerMemoryWrites ? 'left-6' : 'left-1'}`} />
                    </button>
                  </div>
                  <p className="-mt-1 px-1 text-[11px] text-emerald-100/55">Use this when StreamBuddy should connect a moment to a specific chatter, regular, donor, or subscriber.</p>

                  <div className="space-y-1">
                    <label className="block text-xs font-black text-emerald-50">Memory checks per reply</label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={savingGuardrails || guardrailConfig.maxToolCallsPerTurn <= 1}
                        onClick={() => {
                          const next = {
                            ...guardrailConfig,
                            maxToolCallsPerTurn: Math.max(1, guardrailConfig.maxToolCallsPerTurn - 1),
                          };
                          setGuardrailConfig(next);
                           void saveGuardrailConfig(next);
                         }}
                         className="w-8 h-8 rounded-lg bg-black/40 border border-white/10 text-emerald-200 hover:bg-white/10 disabled:opacity-40"
                       >
                         -
                       </button>
                       <input
                         type="text"
                         inputMode="numeric"
                         pattern="[0-9]*"
                         value={guardrailConfig.maxToolCallsPerTurn}
                         onChange={(e) => {
                           const digits = e.target.value.replace(/[^0-9]/g, '');
                           const numeric = digits ? Number(digits) : 3;
                           const next = { ...guardrailConfig, maxToolCallsPerTurn: Math.max(1, Math.min(10, numeric)) };
                           setGuardrailConfig(next);
                         }}
                         onBlur={() => void saveGuardrailConfig(guardrailConfig)}
                         disabled={savingGuardrails}
                         className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-xs text-emerald-100 focus:outline-none focus:border-emerald-500/60 text-center"
                       />
                       <button
                         type="button"
                         disabled={savingGuardrails || guardrailConfig.maxToolCallsPerTurn >= 10}
                         onClick={() => {
                           const next = {
                             ...guardrailConfig,
                             maxToolCallsPerTurn: Math.min(10, guardrailConfig.maxToolCallsPerTurn + 1),
                           };
                           setGuardrailConfig(next);
                           void saveGuardrailConfig(next);
                         }}
                        className="w-8 h-8 rounded-lg bg-black/40 border border-white/10 text-emerald-200 hover:bg-white/10 disabled:opacity-40"
                      >
                        +
                      </button>
                    </div>
                    <p className="text-[11px] text-emerald-100/55">How many saved details StreamBuddy can look up before answering. 3 is usually enough; higher can add more context but may feel busier.</p>
                  </div>
                </div>
              </div>

              {/* Weighted memory preview */}
              <div className="overflow-hidden rounded-[22px] border border-white/10 bg-black/20">
                <div className="p-4 space-y-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h4 className="text-sm font-black text-white">Strongest memories</h4>
                      <p className="mt-1 text-xs text-slate-400">The things StreamBuddy is most likely to remember.</p>
                    </div>
                    <button
                      onClick={() => void runMemoryDecay()}
                      disabled={runningDecay}
                      title="Lowers the priority of older memories that have not been useful lately."
                      className="w-fit rounded-lg border border-cyan-300/25 bg-cyan-400/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-cyan-200 transition-all hover:bg-cyan-400/15 disabled:opacity-50"
                    >
                      {runningDecay ? 'Updating...' : 'Clean Up'}
                    </button>
                  </div>

                  {loadingMemoryEntries ? (
                    <p className="text-gray-500 text-xs">Loading saved memories...</p>
                  ) : memoryEntries.length === 0 ? (
                    <p className="text-gray-500 text-xs">No saved memories yet.</p>
                  ) : (
                    <div className="grid max-h-56 gap-2 overflow-y-auto pr-1 lg:grid-cols-2 2xl:grid-cols-3">
                      {memoryEntries.map((entry) => (
                        <div key={entry.id} className="rounded-2xl border border-white/10 bg-white/[0.025] p-3">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-[9px] font-black uppercase tracking-widest text-cyan-300/80">{entry.category}</span>
                            <span className="text-[9px] font-black uppercase tracking-widest text-gray-500">{Number(entry.salienceScore || 0).toFixed(2)}</span>
                          </div>
                          <p className="text-xs leading-relaxed text-slate-200/85">{entry.factText}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Memory Display/Edit */}
              {streamerConfig.enabled && (
                <div className="bg-black/20 rounded-2xl border border-white/5 overflow-hidden">
                  {isEditing ? (
                    <div className="p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest">Editing notes about you</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => void handleSaveStreamerMemory()}
                            disabled={savingNotes}
                            className="px-3 py-1 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-50"
                          >
                            {savingNotes ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={savingNotes}
                            className="px-3 py-1 bg-white/5 hover:bg-white/10 text-gray-400 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-purple-100 text-sm resize-none focus:outline-none focus:border-purple-500/50 min-h-[200px] font-mono leading-relaxed"
                        placeholder="Streamer vibe: high energy, loves quick jokes, prefers short replies..."
                      />
                    </div>
                  ) : (
                    <div className="p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Saved about you</span>
                        <div className="flex gap-2">
                          <button
                            onClick={handleEditStreamerMemory}
                            className="text-[10px] font-black text-purple-400 uppercase tracking-widest hover:text-purple-300 transition-colors"
                          >
                            {streamerNotes ? 'Edit' : 'Add'}
                          </button>
                          {streamerNotes && (
                            <button
                              onClick={() => setShowClearConfirm(true)}
                              className="text-[10px] font-black text-red-400/60 uppercase tracking-widest hover:text-red-400 transition-colors"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </div>

                      {streamerNotes ? (
                        <div
                          className="bg-purple-500/5 border border-purple-500/10 rounded-xl p-4 cursor-pointer relative group/notes"
                          onClick={() => setNotesRevealed(!notesRevealed)}
                        >
                          <div className={`${notesRevealed ? 'transition-all duration-700' : 'transition-none blur-2xl opacity-0'} ${notesRevealed ? '' : 'blur-2xl opacity-20 grayscale'}`}>
                            {isStructuredFormat(streamerNotes) ? (() => {
                              const parsed = parseStreamerNotes(streamerNotes);
                              const hasContent = Object.keys(parsed).length > 0;
                              if (!hasContent) return <p className="text-gray-500 text-xs italic">No data yet...</p>;
                              return (
                                <div className="grid grid-cols-1 gap-3">
                                  {STREAMER_MEMORY_SECTIONS.map((section) => {
                                    const content = parsed[section.key];
                                    if (!content) return null;
                                    return (
                                      <div key={section.key} className="flex gap-3">
                                        <span className="text-sm shrink-0 opacity-50">{section.icon}</span>
                                        <div className="min-w-0">
                                          <div className="text-[9px] font-black text-purple-400/60 uppercase tracking-widest mb-0.5">{section.label}</div>
                                          <div className="text-purple-100 text-xs leading-relaxed">{content}</div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })() : (
                              <p className="text-purple-100 text-xs leading-relaxed">{streamerNotes}</p>
                            )}
                          </div>
                          {!notesRevealed && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="bg-purple-600/20 backdrop-blur-md px-4 py-2 rounded-full border border-purple-500/30 flex items-center gap-2 shadow-2xl group-hover/notes:scale-105 transition-transform">
                                <span className="text-[10px] font-black text-white/90 uppercase tracking-widest">Reveal saved notes</span>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-8 text-center">
                          <div className="text-2xl mb-2 opacity-20">🧠</div>
                          <p className="text-gray-500 text-xs">Nothing saved yet. StreamBuddy will learn as you use it, or you can add notes manually.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'community' && (
          <div className="space-y-4 animate-in slide-in-from-right-2 duration-300">
            <div className="relative overflow-hidden rounded-[24px] border border-blue-300/[0.14] bg-[linear-gradient(135deg,rgba(15,23,42,0.78),rgba(9,18,42,0.86))] p-4">
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <span className="text-4xl">📜</span>
              </div>
              <h3 className="text-xl font-black text-white">Stream moments</h3>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300/72">
                Memes, big wins, running jokes, and viewer moments StreamBuddy can bring up later.
              </p>
            </div>

            <div className="overflow-hidden rounded-[24px] border border-white/10 bg-black/20">
              <div className="p-4 space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[10px] font-black text-blue-300 uppercase tracking-widest leading-none">Saved stream moments</span>
                  <button
                    onClick={handleEditLore}
                    className="text-[10px] font-black text-blue-300/70 uppercase tracking-widest hover:text-blue-200 transition-colors"
                  >
                    Edit
                  </button>
                  {hasLore && (
                    <button
                      onClick={() => setShowLoreClearConfirm(true)}
                      disabled={clearingLore}
                      className="text-[10px] font-black text-red-400/60 uppercase tracking-widest hover:text-red-400 transition-colors"
                    >
                      {clearingLore ? 'Clearing...' : 'Clear'}
                    </button>
                  )}
                </div>

                <div
                  className={`bg-blue-500/5 border border-blue-500/10 rounded-xl p-4 cursor-pointer relative group/lore min-h-[100px] flex flex-col justify-center ${!hasLore ? 'cursor-default' : ''}`}
                  onClick={() => hasLore && setLoreRevealed(!loreRevealed)}
                >
                  {loadingLore ? (
                    <div className="flex flex-col items-center gap-3 py-6">
                      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Loading moments...</span>
                    </div>
                  ) : hasLore ? (
                    <div className={`transition-all duration-500 space-y-6 ${loreRevealed ? '' : 'blur-xl opacity-20 grayscale'}`}>
                      {/* Channel History */}
                      {communityLore.history.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="text-[10px] font-black text-blue-400/60 uppercase tracking-[0.2em] mb-3">Stream history</h4>
                          <div className="grid gap-2">
                            {communityLore.history.map((event) => (
                              <div key={event.id} className="flex gap-3 bg-white/[0.02] p-2.5 rounded-lg border border-white/5">
                                <span className="text-sm shrink-0 opacity-50">📜</span>
                                <div className="min-w-0">
                                  <div className="text-blue-100 text-xs leading-relaxed">{event.description}</div>
                                  <div className="text-[8px] font-bold text-gray-600 uppercase mt-1">Saved {new Date(event.createdAt).toLocaleDateString()}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Viewer Lore */}
                      {communityLore.viewerLore.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="text-[10px] font-black text-yellow-400/60 uppercase tracking-[0.2em] mb-3">Viewer highlights</h4>
                          <div className="grid gap-2">
                            {communityLore.viewerLore.map((lore) => (
                              <div key={lore.id} className="flex gap-3 bg-white/[0.02] p-2.5 rounded-lg border border-white/5">
                                <span className="text-sm shrink-0 opacity-50">⭐</span>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 mb-0.5">
                                    <span className="text-[10px] font-black text-yellow-500 uppercase tracking-tight">{lore.viewerName}</span>
                                    <span className="text-[8px] font-bold text-gray-600 uppercase">/ {lore.loreType}</span>
                                  </div>
                                  <div className="text-blue-100 text-xs leading-relaxed">{lore.content}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-2xl mb-2 opacity-20">🕯️</div>
                      <p className="text-gray-500 text-xs">No stream moments have been saved yet.</p>
                    </div>
                  )}

                  {hasLore && !loreRevealed && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-blue-600/20 backdrop-blur-md px-4 py-2 rounded-full border border-blue-500/30 flex items-center gap-2 shadow-2xl group-hover/lore:scale-105 transition-transform">
                        <span className="text-[10px] font-black text-white/90 uppercase tracking-widest">Reveal saved moments</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'audience' && (
          <div className="space-y-4 animate-in slide-in-from-bottom-2 duration-300">
            <div className="relative overflow-hidden rounded-[24px] border border-emerald-300/[0.14] bg-[linear-gradient(135deg,rgba(15,23,42,0.78),rgba(8,36,35,0.76))] p-4">
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <span className="text-4xl">👥</span>
              </div>
              <h3 className="text-xl font-black text-white">Viewer memory</h3>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300/72">
                Choose whether StreamBuddy can remember regulars by name and bring up past chats naturally.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-4 rounded-[22px] border border-white/10 bg-black/20 p-4">
                <div>
                  <span className="text-sm font-black text-white">Remember returning viewers</span>
                  <p className="mt-1 text-xs text-slate-400">StreamBuddy can recognize regulars and keep better context.</p>
                </div>
                <button
                  onClick={handleToggle}
                  className={`w-14 h-8 rounded-full transition-colors relative ${config.enabled ? 'bg-green-600 shadow-lg shadow-green-600/20' : 'bg-gray-700'
                    }`}
                >
                  <div
                    className={`w-6 h-6 bg-white rounded-full absolute top-1 transition-transform ${config.enabled ? 'translate-x-7' : 'translate-x-1'
                      }`}
                  />
                </button>
              </div>

              {config.enabled ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-[22px] border border-emerald-300/[0.12] bg-emerald-400/[0.04] p-4">
                    <h4 className="mb-3 text-[10px] font-black uppercase tracking-widest text-emerald-300">What this does</h4>
                    <ul className="space-y-2">
                      {[
                        'Remembers names and past chats',
                        'Keeps light notes for regulars',
                        'Can greet returning viewers naturally'
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs text-emerald-100/70">
                          <span className="text-emerald-400">✓</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-[22px] border border-cyan-300/[0.12] bg-cyan-400/[0.04] p-4">
                    <h4 className="mb-3 text-[10px] font-black uppercase tracking-widest text-cyan-300">Viewer controls</h4>
                    <ul className="space-y-2">
                      <li className="flex flex-col gap-0.5">
                        <code className="text-cyan-300 font-mono text-[10px] font-bold">!mymemory</code>
                        <span className="text-[10px] text-cyan-100/50">See what StreamBuddy remembers</span>
                      </li>
                      <li className="flex flex-col gap-0.5">
                        <code className="text-cyan-300 font-mono text-[10px] font-bold">!forgetme</code>
                        <span className="text-[10px] text-cyan-100/50">Delete their saved memory</span>
                      </li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="rounded-[22px] border border-dashed border-white/10 bg-white/[0.03] p-8 text-center">
                  <p className="text-gray-500 text-xs">Viewer memory is off. StreamBuddy will treat every viewer interaction as fresh.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modals (Ported) */}
      {showClearConfirm && (
        <ClearModal
          title="Clear saved notes about you?"
          desc="StreamBuddy will forget the notes it has saved about you. Viewer memory and stream moments stay untouched."
          onCancel={() => setShowClearConfirm(false)}
          onConfirm={() => void handleClearStreamerMemory()}
          loading={clearingStreamer}
        />
      )}

      {showLoreClearConfirm && (
        <ClearModal
          title="Clear saved stream moments?"
          desc="This deletes saved stream moments, memes, and viewer highlights. This cannot be undone."
          onCancel={() => setShowLoreClearConfirm(false)}
          onConfirm={() => void handleClearCommunityLore()}
          loading={clearingLore}
        />
      )}

      {/* Edit Lore Modal */}
      {isEditingLore && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setIsEditingLore(false)}>
          <div
            className="bg-[#0f0f16] border border-blue-500/20 rounded-3xl p-6 max-w-2xl w-full shadow-2xl animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-white font-black text-xl">Edit stream moments</h2>
                <p className="text-gray-400 text-xs">One moment per line. Keep these short and useful for future streams.</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => void handleSaveLore()}
                  disabled={savingLore}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-50"
                >
                  {savingLore ? 'Saving...' : 'Save Moments'}
                </button>
                <button
                  onClick={() => setIsEditingLore(false)}
                  className="px-4 py-2 bg-white/5 hover:bg-white/10 text-gray-400 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>

            <textarea
              value={editLoreText}
              onChange={(e) => setEditLoreText(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-2xl p-4 text-blue-100 text-sm resize-none focus:outline-none focus:border-blue-500/50 min-h-[300px] font-mono leading-relaxed"
              placeholder="Example: Chat spams 'blue shell energy' after a comeback..."
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Reusable Clear Modal for sleekness
function ClearModal({ title, desc, onCancel, onConfirm, loading }: { title: string, desc: string, onCancel: () => void, onConfirm: () => void, loading: boolean }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div
        className="bg-[#0f0f16] border border-red-500/20 rounded-3xl p-6 max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-12 h-12 rounded-2xl bg-red-500/10 flex items-center justify-center text-xl mb-4 border border-red-500/20">
          ⚠️
        </div>
        <h2 className="text-white font-black text-xl mb-2">{title}</h2>
        <p className="text-gray-400 text-xs leading-relaxed mb-6">{desc}</p>

        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 text-[10px] font-black uppercase tracking-widest transition-all"
          >
            Go Back
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white text-[10px] font-black uppercase tracking-widest transition-all shadow-lg shadow-red-600/20 disabled:opacity-50"
          >
            {loading ? 'Clearing...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
